import { getI18nTranslate, hasI18nTranslation } from '@/utils/i18nUtils'
import { getDomainApiErrorI18nKey, type DomainApiError } from './domain-api-error'

export type AppMessageDescriptor = {
  key: string
  params?: Record<string, unknown>
}

export type AppMessage = string | AppMessageDescriptor

export type DomainErrorHandlingResult = { handled: true } | void

export const translateAppMessage = (message: AppMessage): string => {
  if (typeof message === 'string') {
    return getI18nTranslate(message)
  }

  return getI18nTranslate(message.key, message.params)
}

/** resultCode 有對應翻譯就用該 key，否則 fallback 到通用錯誤文案 */
export const getDefaultDomainErrorMessage = (error: DomainApiError): AppMessage => {
  const resultCodeKey = getDomainApiErrorI18nKey(error)

  if (resultCodeKey && hasI18nTranslation(resultCodeKey)) {
    return resultCodeKey
  }

  return 'Common.Response.Error'
}

export type DomainErrorMessageResolver<TContext> = (
  context: TContext
) => AppMessage | false | undefined

/**
 * 給定 DomainApiError，依 errorMessage 設定決定最終要顯示的翻譯後文字；回傳 null 代表「不顯示」。
 * 供 useAppMutation／useAppQuery 的 onError 分支，以及 fetchQuery/prefetchQuery 一次性 try-catch
 * 情境共用，避免各自複製 default-fallback 邏輯。
 */
export const resolveDomainErrorMessage = <TContext>(
  error: DomainApiError,
  context: TContext,
  options: { errorMessage?: AppMessage | false | DomainErrorMessageResolver<TContext> } = {}
): string | null => {
  const configuredMessage =
    typeof options.errorMessage === 'function'
      ? options.errorMessage(context)
      : options.errorMessage

  if (configuredMessage === false) {
    return null
  }

  return translateAppMessage(configuredMessage ?? getDefaultDomainErrorMessage(error))
}
